/**
 * Google Consent Mode v2 update mapping — docs/CONSENT-MODE.md §3
 *
 * Intentionally absent: gtag('consent', 'default', ...) with all 7 signals
 * denied. Per docs/CONSENT-MODE.md §4 that default state MUST be a separate
 * static inline <script> in the site's <head>, placed before this loader and
 * before any Google tag, copy-pasted from the panel. It is not part of this
 * package so the default never waits on network/bundle load.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

export type GcmSignalValue = 'granted' | 'denied';

/** Seven GCM v2 signals — same keys as gtag('consent', 'update', …). */
export type GoogleConsentState = {
  analytics_storage: GcmSignalValue;
  ad_storage: GcmSignalValue;
  ad_user_data: GcmSignalValue;
  ad_personalization: GcmSignalValue;
  functionality_storage: GcmSignalValue;
  personalization_storage: GcmSignalValue;
  security_storage: GcmSignalValue;
};

/**
 * Build a genuine `arguments` object from a list of values.
 *
 * This is not cosmetic. gtag.js only treats a dataLayer entry as a gtag command
 * when it passes its own check:
 *   Object.prototype.toString.call(x) === '[object Arguments]' || 'callee' in x
 * A plain Array falls into gtag.js's legacy `method.name` dispatch branch, where
 * 'consent' resolves to nothing and the resulting TypeError is swallowed by an
 * internal try/catch. Pushing an Array therefore silently no-ops: consent stays
 * at the page's denied default forever and Google never writes _ga / _ga_* /
 * _gcl_au, no matter how long the visitor waits after "Tümünü Kabul Et".
 */
const captureArguments = function (): IArguments {
  // eslint-disable-next-line prefer-rest-params
  return arguments;
} as (...args: unknown[]) => IArguments;

function gtag(...args: unknown[]): void {
  const w = window as Window & { gtag?: (...a: unknown[]) => void };

  // Prefer the page's own shim (defined by the embed snippet next to
  // gtag('consent','default',…)) so we hit the site's real dataLayer.
  if (typeof w.gtag === 'function') {
    w.gtag(...args);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(captureArguments(...args));
}

/**
 * Pure category → GCM signal mapping (docs/CONSENT-MODE.md §3).
 * Shared by gtag update and consent audit payloads.
 */
export function buildGoogleConsentState(
  acceptedCategory: (category: string) => boolean,
  securityStorageDefault: GcmSignalValue = 'denied',
): GoogleConsentState {
  return {
    analytics_storage: acceptedCategory('analytics') ? 'granted' : 'denied',
    ad_storage: acceptedCategory('advertisement') ? 'granted' : 'denied',
    ad_user_data: acceptedCategory('advertisement') ? 'granted' : 'denied',
    ad_personalization: acceptedCategory('advertisement') ? 'granted' : 'denied',
    functionality_storage: acceptedCategory('functionality') ? 'granted' : 'denied',
    personalization_storage: acceptedCategory('functionality') ? 'granted' : 'denied',
    security_storage: securityStorageDefault,
  };
}

/**
 * Push a GCM v2 consent update based on which CMP categories are accepted.
 */
export function updateGtagConsent(
  acceptedCategory: (category: string) => boolean,
  securityStorageDefault: GcmSignalValue = 'denied',
): void {
  gtag('consent', 'update', buildGoogleConsentState(acceptedCategory, securityStorageDefault));
}
